import React, { useEffect, useState } from 'react';
import './style.less';

interface CheckInNoticeProps {
    title: string;
    body: string;
    // Primary button; omit when there is nowhere useful to send the resident.
    actionLabel?: string;
    onAction?: () => void;
    onClose: () => void;
    // How long it stays up before closing itself.
    autoHideMs?: number;
}

// Banner shown after a check-in that did not notify the family. Slides up from the bottom
// edge and closes itself, on Escape, or with "Got it". Reads as information, not an error:
// the check-in itself worked. The timer waits while the pointer is over the banner or focus is
// inside it, and starts over when they leave, so it never closes under someone who is reading it.
const CheckInNotice: React.FC<CheckInNoticeProps> = ({ title, body, actionLabel, onAction, onClose, autoHideMs = 8000 }) => {
    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);
    const paused = hovered || focused;

    useEffect(() => {
        if (paused) return undefined;
        const timer = window.setTimeout(onClose, autoHideMs);
        return () => window.clearTimeout(timer);
    }, [autoHideMs, onClose, paused]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div
            className="check-in-notice"
            role="status"
            aria-live="polite"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onFocus={() => setFocused(true)}
            onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false);
            }}
        >
            <span className="check-in-notice-icon" aria-hidden="true">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
            </span>
            <span className="check-in-notice-text">
                <span className="check-in-notice-title">{title}</span>
                <span className="check-in-notice-body">{body}</span>
            </span>
            <span className="check-in-notice-actions">
                <button type="button" className="check-in-notice-btn ghost" onClick={onClose}>
                    Got it
                </button>
                {actionLabel && onAction && (
                    <button type="button" className="check-in-notice-btn primary" onClick={onAction}>
                        {actionLabel}
                    </button>
                )}
            </span>
        </div>
    );
};

export default CheckInNotice;
